import { type Root, tree } from "./markdown";

/**
 * A container directive written on one line, `:::callout{tone=warning} A note. :::`, is not one to
 * the parser: it wants the opening fence alone on its line, the content after it and the closing
 * fence alone. The syntax line a model is shown for `card` and `callout` reads as one line, and a
 * production answer copied it (chat/12 says how the lines go), so answers already stored hold the
 * one-line form. This puts the lines where they belong, for the text the engine is given.
 *
 * Only a whole line is read that way: the same number of colons at its start and at its end, a
 * lowercase name straight after the first, and something between. A line inside code (a fenced
 * block, however it is nested in lists and quotes, or an inline span that runs over lines) is
 * left as written, and which lines those are is the Markdown parser's answer, not a scan of this
 * file's own: the text is parsed once, and only when a line looks like the one-line form. The
 * result is the text with line breaks added, never the text with anything removed, so a line that
 * does not match stays byte for byte what it was, and a text with no such line is returned as the
 * same string.
 */
const ONE_LINE =
  /^( {0,3})(:{3,})([a-z][a-z0-9-]*)(\[[^\]\n]*\])?(\{[^}\n]*\})?[ \t]+(\S.*?)[ \t]+\2[ \t]*$/;

type Node = Root["children"][number];

/** The first and last line (counting from 1) of every piece of code in the text, as parsed. */
function codeLines(text: string): [number, number][] {
  // The engine's own reading (markdown.ts), so both agree on what is code: HTML and indented code
  // are off there, so neither hides a fence here.
  const root = tree(text);
  const ranges: [number, number][] = [];
  const visit = (node: Node | Root) => {
    if ((node.type === "code" || node.type === "inlineCode") && node.position) {
      ranges.push([node.position.start.line, node.position.end.line]);
    }
    if ("children" in node) for (const child of node.children) visit(child);
  };
  visit(root);
  return ranges;
}

export function fenceOneLine(text: string): string {
  // Most answers have no directive at all, and the engine runs on every chunk of a stream.
  if (!text.includes(":::")) return text;
  const lines = text.split("\n");
  const candidates = lines.flatMap((line, index) => (ONE_LINE.test(line) ? [index] : []));
  if (candidates.length === 0) return text;
  const code = codeLines(text);
  let changed = false;
  for (const index of candidates) {
    const line = index + 1;
    if (code.some(([first, last]) => line >= first && line <= last)) continue;
    const found = ONE_LINE.exec(lines[index] ?? "");
    if (found === null) continue;
    const [, indent = "", colons = "", name = "", label = "", attrs = "", content = ""] = found;
    lines[index] =
      `${indent}${colons}${name}${label}${attrs}\n${indent}${content}\n${indent}${colons}`;
    changed = true;
  }
  return changed ? lines.join("\n") : text;
}
